// Conversation types
export enum ConversationType {
  DIRECT = 'direct',
  GROUP = 'group',
  PRIVATE_GROUP = 'private_group',
  COURSE = 'course',
  COMMUNITY = 'community',
}

export enum MessageType {
  TEXT = 'text',
  IMAGE = 'image',
  FILE = 'file',
  AUDIO = 'audio',
  VIDEO = 'video',
}

export enum MessageStatus {
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
  FAILED = 'failed',
  PENDING = 'pending',
  ERROR = 'error',
}

export enum ConversationStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  DELETED = 'deleted',
}

// New enums for conversation participants
export enum ParticipantStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  LEFT = 'left',
  REMOVED = 'removed',
}

export enum ParticipantRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  MODERATOR = 'moderator',
}

export interface Conversation {
  id: number
  hiringId?: number
  name?: string
  description?: string
  avatar?: string
  status?: ConversationStatus
  type?: ConversationType
  createdAt: string
  updatedAt: string
  participants: ConversationParticipant[]
  lastMessageContent?: string
  lastMessageAt?: string
  lastMessage?: Message
  unreadCount: number
  isPinned?: boolean
  pinnedAt?: string
}

export interface ConversationParticipant {
  id: number
  conversationId: number
  userId: number
  status: ParticipantStatus
  role: ParticipantRole
  joinedAt: string | null
  leftAt: string | null
  lastReadAt: string | null
  // User information (from joined user entity)
  user: {
    id: number
    firstName: string
    lastName: string
    email: string
    avatar?: string
    profileId?: string
    lastActiveAt?: string | null
  }
}

export interface CreateConversationDto {
  participantIds: number[]
  type?: ConversationType
  name?: string
  description?: string
  avatar?: string
  addAllUsers?: boolean
  courseIds?: number[]
}

export interface UpdateConversationDto {
  name?: string
  description?: string
  avatar?: string
  status?: ConversationStatus
}

export interface UpdateParticipantRoleDto {
  conversationId: number
  participantId: number
  newRole: ParticipantRole
}

export interface SearchConversationsDto {
  q?: string
  limit?: number
  offset?: number
}

// Message types
export interface MessageKeysetListMeta {
  limit: number
  hasPrevious: boolean
  previousCursor: string | null
  hasMore: boolean
  nextCursor: string | null
}

export interface MessageKeysetListResponse {
  data: Message[]
  meta: MessageKeysetListMeta
}

export interface Message {
  id: number
  content: string
  type: MessageType
  status: MessageStatus
  sender: {
    id: number
    firstName: string
    lastName: string
    avatar?: string
    profileId?: string
  }
  conversation: {
    id: number
  }
  fileUrl?: string
  fileName?: string
  fileSize?: number
  fileType?: string
  metadata?: Record<string, unknown>
  isEdited: boolean
  editedAt?: string
  isDeleted: boolean
  deletedAt?: string
  createdAt: string
  updatedAt: string
  // Reply message properties
  repliedMessage?: {
    id: number
    content: string
    type: MessageType
    sender: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
      profileId?: string
    }
    fileUrl?: string
    fileName?: string
  }
  // Frontend convenience properties (not in backend schema)
  userName?: string
  userAvatar?: string
  isSystem?: boolean
  // Error handling properties
  errorMessage?: string
  isOptimistic?: boolean
  tempId?: string
  // Reaction properties
  reactions?: MessageReaction[]
  reactionCount?: number
  userReacted?: boolean
  // Pinned message properties (joined relation from backend)
  pinnedMessage?: {
    pinnedAt: string
    pinnedByUser: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
      profileId?: string
    }
  }
}

export interface CreateMessageDto {
  conversationId: number
  content: string
  taggedUserIds?: number[]
  type?: MessageType
  fileUrl?: string
  fileName?: string
  fileSize?: number
  fileType?: string
  metadata?: Record<string, unknown>
}

export interface CreateReplyMessageDto {
  conversationId: number
  repliedMessageId: number
  content: string
  taggedUserIds?: number[]
  type?: MessageType
  fileUrl?: string
  fileName?: string
  fileSize?: number
  fileType?: string
  metadata?: Record<string, unknown>
}

export interface UpdateMessageDto {
  content?: string
  type?: MessageType
  fileUrl?: string
  fileName?: string
  fileType?: string
  metadata?: Record<string, unknown>
}

// WebSocket types for real-time features
export interface ChatMessage {
  id: string
  conversationId: number
  userId: number
  userName: string
  userAvatar?: string
  content: string
  timestamp: string
  isSystem?: boolean
}

export interface TypingIndicator {
  conversationId: number
  userId: number
  userName: string
  isTyping: boolean
}

export interface OnlineUsersUpdate {
  conversationId: number
  userIds: number[]
}

export interface ChatRoomJoin {
  conversationId: number
  userId: number
  userName: string
  userAvatar?: string
  isJobOwnerOrAdmin: boolean
}

export interface ChatRoomLeave {
  conversationId: number
  userId: number
  userName: string
}

export interface SystemMessage {
  conversationId: number
  content: string
  type: 'CV_UPLOADED' | 'CV_REVIEWED' | 'CV_ACCEPTED' | 'CV_REJECTED' | 'USER_JOINED' | 'USER_LEFT'
  userId?: number
  userName?: string
}

// Delete message scope enum
export enum DeleteMessageScope {
  FOR_ME = 'for_me',
  FOR_EVERYONE = 'for_everyone',
}

// Delete message DTO
export interface DeleteMessageDto {
  scope: DeleteMessageScope
}

export interface PinnedMessage {
  id: number
  conversationId: number
  messageId: number
  pinnedBy: number
  pinnedAt: string
  message: Message
  pinnedByUser: {
    id: number
    firstName: string
    lastName: string
    avatar?: string
    profileId?: string
  }
}

export interface ChatNotification {
  type: 'new_message' | 'system_message' | 'typing_indicator' | 'online_users'
  data: ChatMessage | SystemMessage | TypingIndicator | OnlineUsersUpdate
}

export interface ChatRoomState {
  messages: Message[]
  onlineUsers: Set<number>
  isTyping: boolean
  isConnected: boolean
  lastMessageTimestamp?: string
}

export interface ChatConfig {
  maxMessageLength: number
  typingTimeout: number
  reconnectAttempts: number
  heartbeatInterval: number
}

export const DEFAULT_CHAT_CONFIG: ChatConfig = {
  maxMessageLength: 500,
  typingTimeout: 3000,
  reconnectAttempts: 5,
  heartbeatInterval: 30000,
} as const

// API Response types
export interface GetUnreadCountsResponse {
  [conversationId: number]: number
}

// Message reaction types
export type MessageReactionType = 'like' | 'love' | 'haha' | 'wow' | 'sad' | string

export type MessageEmojiData = {
  unicode?: string
  shortcode?: string
  imageUrl?: string
  name?: string
}

export interface MessageReaction {
  id: number
  reactionType: MessageReactionType
  emojiData?: MessageEmojiData
  user: {
    id: number
    firstName: string
    lastName: string
    avatar?: string | null
  }
  createdAt: string
}

export interface CreateMessageReactionDto {
  messageId: number
  reactionType: MessageReactionType
  emojiData?: MessageEmojiData
}

export interface DeleteMessageReactionDto {
  messageId: number
  reactionType?: MessageReactionType
  emojiData?: MessageEmojiData
}

export interface MessageReactionGroup {
  identifier: string
  reactionType: MessageReactionType
  emojiData?: MessageEmojiData
  count: number
  users: Array<{ id: number; name: string }>
  userReacted: boolean
  isDefault: boolean
}

/**
 * Chat message data structure for WebSocket events
 * This matches the backend message-sent event data structure
 */
export interface ChatMessageData {
  id: number
  content: string
  type: MessageType
  status: MessageStatus
  sender: {
    id: number
    firstName: string
    lastName: string
    avatar?: string
    profileId?: string
  }
  conversation: {
    id: number
  }
  fileUrl?: string
  fileName?: string
  fileSize?: number
  fileType?: string
  metadata?: Record<string, unknown>
  isEdited: boolean
  editedAt?: string
  isDeleted: boolean
  deletedAt?: string
  createdAt: string
  updatedAt: string
  // Reply message properties
  repliedMessage?: {
    id: number
    content: string
    type: MessageType
    sender: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
      profileId?: string
    }
    fileUrl?: string
    fileName?: string
  }
  // Frontend convenience properties
  userName?: string
  userAvatar?: string
  isSystem?: boolean
  // Error handling properties
  errorMessage?: string
  isOptimistic?: boolean
  tempId?: string
  isEmptyConversation?: boolean
  // Reaction properties
  reactions?: MessageReaction[]
  reactionCount?: number
  userReacted?: boolean
  // Pinned message properties (joined relation from backend)
  pinnedMessage?: {
    pinnedAt: string
    pinnedByUser: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
      profileId?: string
    }
  }
}
